import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Hotel, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { strBenchmarkFor, STR_BY_VALUE } from "../lib/strBenchmarks";

/**
 * Hospitality Pulse — landlord dashboard chip showing how many hotels/motels
 * are under-performing their declared STR segment. Surfaces under-priced ADR
 * opportunities at a glance.
 *
 * Hides itself completely when:
 *   - the user has zero hotels/motels in their portfolio, OR
 *   - none of their hotels have both a `str_segment` AND ADR+occupancy set
 *     (without those, there's nothing to compare)
 */
export default function HospitalityPulseWidget() {
  const [hotels, setHotels] = useState(null); // null = loading

  useEffect(() => {
    let alive = true;
    api.get("/properties")
      .then((r) => {
        if (!alive) return;
        const all = r.data || [];
        // Only hotels/motels with the data we need to benchmark
        const eligible = all.filter(
          (p) =>
            (p.property_type === "hotel" || p.property_type === "motel") &&
            p.str_segment &&
            p.adr != null &&
            p.occupancy_pct != null,
        );
        setHotels(eligible);
      })
      .catch(() => setHotels([]));
    return () => { alive = false; };
  }, []);

  if (hotels == null) return null; // loading
  if (hotels.length === 0) return null; // hide widget entirely

  // Compute below/in-range/above buckets
  let below = 0, inRange = 0, above = 0;
  let worstName = null;
  let worstGap = 0;
  for (const h of hotels) {
    const revpar = Number(h.adr) * Number(h.occupancy_pct) / 100;
    const b = strBenchmarkFor(h.str_segment, revpar);
    if (b.status === "below") {
      below += 1;
      // Track the most under-performing hotel for the highlight callout
      if (b.gap_pct < worstGap) {
        worstGap = b.gap_pct;
        worstName = h.name;
      }
    } else if (b.status === "above") {
      above += 1;
    } else if (b.status === "in_range") {
      inRange += 1;
    }
  }
  const total = hotels.length;
  const tone = below > 0 ? "warn" : "ok";
  const cardCls =
    tone === "warn"
      ? "border-amber-300 bg-amber-50/60"
      : "border-emerald-200 bg-emerald-50/40";
  const accentCls = tone === "warn" ? "text-amber-700" : "text-emerald-700";

  return (
    <Link
      to="/dashboard/properties"
      className={`block rounded-sm border ${cardCls} p-4 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200`}
      data-testid="hospitality-pulse-widget"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Hotel className={`w-4 h-4 ${accentCls}`} />
          <div className="overline text-slate-700">Hospitality pulse</div>
        </div>
        <ArrowRight className="w-4 h-4 text-slate-400" />
      </div>

      <div className="mt-2">
        {below > 0 ? (
          <>
            <div className={`text-2xl font-bold tracking-tight font-mono ${accentCls}`} data-testid="hospitality-pulse-headline">
              {below} <span className="text-base font-normal text-slate-700">of {total}</span>
              <span className="text-sm font-normal text-slate-700"> hotel{total === 1 ? "" : "s"} under-performing</span>
            </div>
            {worstName && worstGap < 0 && (
              <div className="mt-1 text-xs text-slate-600">
                Worst:{" "}
                <span className="font-semibold text-slate-900">{worstName}</span>{" "}
                <span className={accentCls}>({worstGap.toFixed(1)}% vs segment midpoint)</span>
              </div>
            )}
          </>
        ) : (
          <div className="text-2xl font-bold tracking-tight font-mono text-emerald-700" data-testid="hospitality-pulse-headline">
            {total === 1 ? "Hotel pacing segment" : `All ${total} hotels pacing segment`}
          </div>
        )}
      </div>

      <div className="mt-3 flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-slate-500">
        <span className="inline-flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> {below} below
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-cyan-500" /> {inRange} in-range
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {above} above
        </span>
      </div>

      {below > 0 && (
        <div className="mt-2 text-[11px] text-slate-600">
          Tap to filter your hotels and review pricing →
        </div>
      )}
    </Link>
  );
}

// Re-export to keep the lint linter happy when STR_BY_VALUE is unused in this
// file — used elsewhere in the chain. Not strictly required, but keeps the
// import block consistent if this file grows.
void STR_BY_VALUE;
